/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

export type ReportContextSource = 'url' | 'xml' | 'none';

export interface ResolvedReportContext {
  readonly reportId?: string;
  readonly matterId?: string;
  readonly source: ReportContextSource;
}

/**
 * Resolve the report context for the task pane binding.
 *
 * Precedence:
 *   1. Task pane URL params (primary, synchronous)
 *   2. Custom XML from the opened document (fallback, asynchronous)
 *   3. No context found (returns empty; the caller shows the dashboard)
 *
 * The custom XML source uses urn:ecsper:document:1, reading reportId
 * and projectId (which maps to matterId in the Workspace domain).
 */
export async function resolveReportContext(): Promise<ResolvedReportContext> {
  const params = new URLSearchParams(window.location.search);
  const urlReportId = params.get('reportId') || undefined;
  const urlMatterId = params.get('matterId') || params.get('projectId') || undefined;

  if (urlMatterId !== undefined || urlReportId !== undefined) {
    return {
      ...(urlReportId !== undefined ? { reportId: urlReportId } : {}),
      ...(urlMatterId !== undefined ? { matterId: urlMatterId } : {}),
      source: 'url',
    };
  }

  try {
    const { readDocumentContext } = await import('@ecsper/document-context');
    const context = await readDocumentContext();

    if (context !== null) {
      const xmlMatterId = context.projectId || undefined;
      const xmlReportId = context.reportId || undefined;

      if (xmlMatterId !== undefined) {
        return {
          ...(xmlReportId !== undefined ? { reportId: xmlReportId } : {}),
          matterId: xmlMatterId,
          source: 'xml',
        };
      }
    }
  } catch {
    // Custom XML not available (no document open, or XML not embedded)
  }

  return { source: 'none' };
}
